/**
 * Per-entity reference checks for the reference audit (dispatch by `e.type`, §4).
 *
 * Package-private to the `referenceAudit*` module family. `checkEntityRefs`
 * resolves every symbol-table / handle / entity-id reference an individual
 * entity (or block child) carries; `checkAnnotativeContexts` factors out the
 * shared annotative-context scaleName loop. `Container` carries the owning
 * space / block-definition identity (F5).
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §4 / §5.3 / §7.
 */

import type {
  BlockChildEntity,
  DrawingEntity,
  RawEntityGroup,
} from "./model";
import {
  checkHandle,
  checkMleaderArrowheadRef,
  checkName,
} from "./referenceAudit.checkers";
import type { Resolution } from "./referenceAudit.resolution";
import {
  SENT_ARROWHEAD,
  SENT_LINETYPE,
  SENT_NONE,
} from "./referenceAudit.sentinels";
import {
  ReferenceErrorCode,
  type IssueContext,
  type OwnerKind,
  type RefKind,
  type ReferenceIssue,
} from "./referenceAudit.types";
// The SINGLE source of truth for the ARRAYRECT→MINSERT emit decision — the same
// predicate the exporter (dxfBridge.arrayMinsert) consumes. Reused here so the
// audit flags `sourceBlockName` EXACTLY when the export will emit a MINSERT of
// it, never for an associative / unresolved-extension-dictionary / 1×1 array
// whose group-2 is inert export metadata (@codex SL-18 R3 P2).
import { arrayRectEmitsAsMInsert } from "./dxfBridge.arrayMinsert";

export interface Container {
  id: string;
  kind: OwnerKind;
  entities: ReadonlyArray<DrawingEntity | BlockChildEntity>;
}

/** Common base for every annotative-context array carrying a scaleName. */
export function checkAnnotativeContexts(
  contexts: ReadonlyArray<{ scaleName?: string }>,
  res: Resolution,
  ownerKind: OwnerKind,
  ownerId: string,
  container: string,
  errors: ReferenceIssue[],
): void {
  contexts.forEach((ctxItem, i) => {
    // scaleName is optional (Dimension/Multileader contexts can default to
    // "") — skip empties; only resolve non-empty names. Annotative context
    // scale names are advisory; an empty one means "the implicit context".
    checkName(
      ctxItem.scaleName,
      res.names.annotationScales,
      SENT_NONE,
      false,
      ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
      {
        ownerKind,
        ownerId,
        container,
        path: `annotativeContexts[${i}].scaleName`,
        field: "annotativeContexts.scaleName",
      },
      errors,
    );
  });
}

export function checkEntityRefs(
  e: DrawingEntity | BlockChildEntity,
  c: Container,
  localIds: ReadonlySet<string>,
  res: Resolution,
  errors: ReferenceIssue[],
): void {
  const ownerKind = c.kind;
  const ownerId = e.id;
  const container = c.id;
  const base = (path: string, field: string): IssueContext => ({
    ownerKind,
    ownerId,
    container,
    path,
    field,
  });

  // ── Field 1: entity.layer → layers (required). ────────────────────────
  checkName(
    e.layer,
    res.names.layers,
    SENT_NONE,
    true,
    ReferenceErrorCode.UNDEFINED_LAYER,
    base("layer", "layer"),
    errors,
  );

  // ── Field 2: entity.linetype → linetypes (required). ──────────────────
  // Enabled after PR-LT2 (typed LTYPE parsing — plan §10.5). `SENT_LINETYPE`
  // bypasses ByLayer / ByBlock / Continuous; any other non-empty name must
  // resolve against the (seed-merged) linetype table.
  checkName(
    e.linetype,
    res.names.linetypes,
    SENT_LINETYPE,
    true,
    ReferenceErrorCode.UNDEFINED_LINETYPE,
    base("linetype", "linetype"),
    errors,
  );

  switch (e.type) {
    case "Text": {
      checkName(
        e.styleName,
        res.names.textStyles,
        SENT_NONE,
        true,
        ReferenceErrorCode.UNDEFINED_TEXT_STYLE,
        base("styleName", "styleName"),
        errors,
      );
      checkName(
        e.creationScaleName,
        res.names.annotationScales,
        SENT_NONE,
        false,
        ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
        base("creationScaleName", "creationScaleName"),
        errors,
      );
      checkAnnotativeContexts(
        e.annotativeContexts,
        res,
        ownerKind,
        ownerId,
        container,
        errors,
      );
      break;
    }
    case "MText": {
      checkName(
        e.styleName,
        res.names.textStyles,
        SENT_NONE,
        true,
        ReferenceErrorCode.UNDEFINED_TEXT_STYLE,
        base("styleName", "styleName"),
        errors,
      );
      checkName(
        e.creationScaleName,
        res.names.annotationScales,
        SENT_NONE,
        false,
        ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
        base("creationScaleName", "creationScaleName"),
        errors,
      );
      checkAnnotativeContexts(
        e.annotativeContexts,
        res,
        ownerKind,
        ownerId,
        container,
        errors,
      );
      break;
    }
    case "Dimension": {
      checkName(
        e.styleName,
        res.names.dimensionStyles,
        SENT_NONE,
        true,
        ReferenceErrorCode.UNDEFINED_DIMENSION_STYLE,
        base("styleName", "styleName"),
        errors,
      );
      // anonymousBlockName — optional block ref (≠ ""); empty = none.
      checkName(
        e.anonymousBlockName,
        res.names.blocks,
        SENT_NONE,
        false,
        ReferenceErrorCode.UNDEFINED_BLOCK,
        base("anonymousBlockName", "anonymousBlockName"),
        errors,
      );
      // styleOverrides.dimblk / dimblk1 / dimblk2 — optional arrowhead block.
      const ov = e.styleOverrides;
      checkName(
        ov.dimblk,
        res.names.blocks,
        SENT_ARROWHEAD,
        false,
        ReferenceErrorCode.UNDEFINED_BLOCK,
        base("styleOverrides.dimblk", "styleOverrides.dimblk"),
        errors,
      );
      checkName(
        ov.dimblk1,
        res.names.blocks,
        SENT_ARROWHEAD,
        false,
        ReferenceErrorCode.UNDEFINED_BLOCK,
        base("styleOverrides.dimblk1", "styleOverrides.dimblk1"),
        errors,
      );
      checkName(
        ov.dimblk2,
        res.names.blocks,
        SENT_ARROWHEAD,
        false,
        ReferenceErrorCode.UNDEFINED_BLOCK,
        base("styleOverrides.dimblk2", "styleOverrides.dimblk2"),
        errors,
      );
      // dimblk*Handle — IMPORTED soft-pointer handles (SL-11 round-3 provenance
      // twins) to arrowhead BLOCK_RECORDs. Validate each against the BLOCK_RECORD
      // handle set (R10) so a handle whose record was deleted/purged SURFACES as
      // a POINTER_TARGET_NOT_EXIST (DANGLING_HANDLE) error instead of auditing
      // clean while the export stamp silently DROPS the whole DSTYLE override (a
      // dangling imported `1005` has no live record → `resolveArrowheadValue`
      // returns undefined). The null handle "0" is the CLOSEDFILLED default —
      // mapped by the stamp to the default arrow, never a record lookup — so it
      // is NOT a dangling reference; `checkHandle` already skips empty/absent.
      //
      // SL-11 round-6 (@codex F4) — the NAME override WINS on export. When the
      // corresponding `dimblk*` name is DEFINED, `buildDimensionStyleOverridesXData`
      // emits the `1000 <name>` placeholder and IGNORES the handle twin (it emits
      // `1005 <handle>` ONLY when the name is `undefined`). So a stale handle left
      // beside a set name is DEAD — never emitted — and must NOT fail pre-export
      // audit as a dangling reference. Skip it here, mirroring that exact precedence.
      for (const [handle, name, field] of [
        [ov.dimblkHandle, ov.dimblk, "styleOverrides.dimblkHandle"],
        [ov.dimblk1Handle, ov.dimblk1, "styleOverrides.dimblk1Handle"],
        [ov.dimblk2Handle, ov.dimblk2, "styleOverrides.dimblk2Handle"],
      ] as const) {
        if (name !== undefined) continue; // name override wins → handle is dead
        if (handle === "0") continue;
        checkHandle(
          handle,
          res.handles.blockRecords,
          false,
          ReferenceErrorCode.DANGLING_HANDLE,
          base(field, field),
          errors,
        );
      }
      checkAnnotativeContexts(
        e.annotativeContexts,
        res,
        ownerKind,
        ownerId,
        container,
        errors,
      );
      break;
    }
    case "Multileader": {
      checkName(
        e.styleName,
        res.names.multileaderStyles,
        SENT_NONE,
        true,
        ReferenceErrorCode.UNDEFINED_MULTILEADER_STYLE,
        base("styleName", "styleName"),
        errors,
      );
      checkName(
        e.textStyleName,
        res.names.textStyles,
        SENT_NONE,
        true,
        ReferenceErrorCode.UNDEFINED_TEXT_STYLE,
        base("textStyleName", "textStyleName"),
        errors,
      );
      // arrowheadBlock — optional arrowhead block. MLEADER arrowheads export
      // VERBATIM (body 342) with NO built-in synthesis (SL-11 round-6 @codex): a
      // declared block NAME / a live BLOCK_RECORD handle / the closed-filled default
      // resolves clean; an unprefixed built-in like `OPEN90` with no real block
      // dangles on reopen → flagged. NOT the DIMENSION `SENT_ARROWHEAD` whitelist.
      checkMleaderArrowheadRef(
        e.arrowheadBlock,
        res.names.blocks,
        res.handles.blockRecords,
        "entity", // body-342 written verbatim → a live BLOCK_RECORD handle is valid
        ReferenceErrorCode.UNDEFINED_BLOCK,
        base("arrowheadBlock", "arrowheadBlock"),
        errors,
      );
      // leaderRoots[].nodes[].lines[].arrowheadBlock — optional arrowhead (same
      // verbatim-export semantics as the entity-level arrowheadBlock above).
      e.leaderRoots.forEach((root, ri) => {
        root.nodes.forEach((node, ni) => {
          node.lines.forEach((line, li) => {
            checkMleaderArrowheadRef(
              line.arrowheadBlock,
              res.names.blocks,
              res.handles.blockRecords,
              "entity", // leader-line 342 written verbatim → a live handle is valid
              ReferenceErrorCode.UNDEFINED_BLOCK,
              base(
                `leaderRoots[${ri}].nodes[${ni}].lines[${li}].arrowheadBlock`,
                "leaderLine.arrowheadBlock",
              ),
              errors,
            );
          });
        });
      });
      // blockName → blocks (block-content reference). Required for
      // block-content leaders (contentType 1): an empty blockName leaves a
      // block-content multileader with nothing to render (Codex #199 P2 —
      // mirrors the style-level MLeaderStyle.blockName fix in structural.ts).
      // Optional for text-content leaders (contentType 2) where empty = text.
      checkName(
        e.blockName,
        res.names.blocks,
        SENT_NONE,
        e.contentType === 1,
        ReferenceErrorCode.UNDEFINED_BLOCK,
        base("blockName", "blockName"),
        errors,
      );
      // blockAttributeReferences[].attDefHandle — content-gated (F6):
      // resolve ONLY when this is block content (contentType === 1) AND the
      // blockName resolves; against THAT block's ATTDEF-child handles.
      const blockNameKey = e.blockName ? e.blockName.toLowerCase() : "";
      const isBlockContent = e.contentType === 1;
      if (
        isBlockContent &&
        blockNameKey !== "" &&
        res.names.blocks.has(blockNameKey)
      ) {
        const attdefHandles =
          res.attdefByBlock.get(blockNameKey) ?? new Set<string>();
        e.blockAttributeReferences.forEach((ref, i) => {
          checkHandle(
            ref.attDefHandle,
            attdefHandles,
            true,
            ReferenceErrorCode.DANGLING_ATTDEF_HANDLE,
            base(
              `blockAttributeReferences[${i}].attDefHandle`,
              "attDefHandle",
            ),
            errors,
          );
        });
      }
      checkAnnotativeContexts(
        e.annotativeContexts,
        res,
        ownerKind,
        ownerId,
        container,
        errors,
      );
      break;
    }
    case "Hatch": {
      // patternName (field 20) is BYPASS — pattern library, not a table.
      checkName(
        e.creationScaleName,
        res.names.annotationScales,
        SENT_NONE,
        false,
        ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
        base("creationScaleName", "creationScaleName"),
        errors,
      );
      checkAnnotativeContexts(
        e.annotativeContexts,
        res,
        ownerKind,
        ownerId,
        container,
        errors,
      );
      break;
    }
    case "Block": {
      // INSERT.blockName → blocks (REQUIRED → UNDEFINED_BLOCK on empty).
      checkName(
        e.blockName,
        res.names.blocks,
        SENT_NONE,
        true,
        ReferenceErrorCode.UNDEFINED_BLOCK,
        base("blockName", "blockName"),
        errors,
      );
      // attributes[].overrideStyle → textStyles (optional).
      e.attributes.forEach((attr, i) => {
        checkName(
          attr.overrideStyle,
          res.names.textStyles,
          SENT_NONE,
          false,
          ReferenceErrorCode.UNDEFINED_TEXT_STYLE,
          base(
            `attributes[${i}].overrideStyle`,
            "attributes.overrideStyle",
          ),
          errors,
        );
      });
      // annotationScales[] (allow-list) → annotationScales.
      e.annotationScales.forEach((scaleName, i) => {
        checkName(
          scaleName,
          res.names.annotationScales,
          SENT_NONE,
          false,
          ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
          base(`annotationScales[${i}]`, "annotationScales"),
          errors,
        );
      });
      checkName(
        e.creationScaleName,
        res.names.annotationScales,
        SENT_NONE,
        false,
        ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
        base("creationScaleName", "creationScaleName"),
        errors,
      );
      checkAnnotativeContexts(
        e.annotativeContexts,
        res,
        ownerKind,
        ownerId,
        container,
        errors,
      );
      // representationOverrides.representationDataHandle — raw OBJECTS
      // handle, unverifiable (field 26) — intentionally skipped.
      break;
    }
    case "Attdef": {
      checkName(
        e.styleName,
        res.names.textStyles,
        SENT_NONE,
        true,
        ReferenceErrorCode.UNDEFINED_TEXT_STYLE,
        base("styleName", "styleName"),
        errors,
      );
      checkName(
        e.creationScaleName,
        res.names.annotationScales,
        SENT_NONE,
        false,
        ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
        base("creationScaleName", "creationScaleName"),
        errors,
      );
      checkAnnotativeContexts(
        e.annotativeContexts,
        res,
        ownerKind,
        ownerId,
        container,
        errors,
      );
      break;
    }
    case "Rectangular Associative Array": {
      // sourceEntityIds[] → entity ids in the OWNING container (F5).
      e.sourceEntityIds.forEach((srcId, i) => {
        if (!localIds.has(srcId)) {
          errors.push({
            ...base(`sourceEntityIds[${i}]`, "sourceEntityIds"),
            code: ReferenceErrorCode.DANGLING_ARRAY_SOURCE,
            refKind: "entityId" as RefKind,
            referencedKey: srcId,
            message: `array source entity "${srcId}" not found in ${container}`,
          });
        }
      });
      // SL-18 — `sourceBlockName` (DXF group 2) is a REAL BLOCK-table reference
      // ONLY for an array that actually re-emits as a genuine MINSERT of that
      // block (dxfBridge.arrayMinsert): a missing block then means the export
      // silently falls back to the grid-losing `0 ARRAYRECT` path. Audit it as
      // an OPTIONAL block ref (absent for reducer-built arrays → skipped) —
      // case-insensitively, like the INSERT `blockName` ref (@codex SL-18 P2).
      //
      // GATED on the exporter's own `arrayRectEmitsAsMInsert` predicate (not a
      // bare `associative === false`): an ASSOCIATIVE array, an array with an
      // UNRESOLVED extension dictionary, or a 1×1 array all deliberately keep the
      // `0 ARRAYRECT` block reference, so `sourceBlockName` is NOT a live INSERT
      // ref and auditing it would report a spurious UNDEFINED_BLOCK for the
      // anonymous `*U…` anchor / preserved-array source those paths never emit
      // (@codex SL-18 R3 P2). `sourceBlockDefined = true` here: the predicate
      // decides emit-SHAPE eligibility (non-assoc, no assoc graph, real grid,
      // real source name) and `checkName` then verifies the block actually
      // exists — a shape-eligible array whose source block is genuinely absent is
      // a real dangling reference worth flagging.
      if (arrayRectEmitsAsMInsert(e, true)) {
        checkName(
          e.sourceBlockName,
          res.names.blocks,
          SENT_NONE,
          false,
          ReferenceErrorCode.UNDEFINED_BLOCK,
          base("sourceBlockName", "sourceBlockName"),
          errors,
        );
      }
      break;
    }
    case "Viewport": {
      // frozenLayers[] → layers (optional).
      e.frozenLayers.forEach((layerName, i) => {
        checkName(
          layerName,
          res.names.layers,
          SENT_NONE,
          false,
          ReferenceErrorCode.UNDEFINED_LAYER,
          base(`frozenLayers[${i}]`, "frozenLayers"),
          errors,
        );
      });
      // viewportCannoscaleName → annotationScales (optional).
      checkName(
        e.viewportCannoscaleName,
        res.names.annotationScales,
        SENT_NONE,
        false,
        ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE,
        base("viewportCannoscaleName", "viewportCannoscaleName"),
        errors,
      );
      break;
    }
    // NOTE on placement: this arm sits at the END of the switch, after
    // "Viewport", rather than next to "Block" whose blockName it mirrors.
    // Inserting it between two existing arms re-identified a PRE-EXISTING,
    // already-baselined duplicate block in this file — the jscpd fragment for
    // the MText/Attdef style+annotation-scale clone ends at the NEXT `case`
    // token, so a new neighbour rehashes that clone into a "new" one without
    // any code being copied. Keeping the arm here leaves that tracked debt
    // identified exactly as the baseline records it. Behaviour is unaffected:
    // every arm ends in `break`, so order carries no meaning.
    case "Table": {
      // ACAD_TABLE group 2 → blocks. The anonymous `*T<N>` block carrying the
      // table's rendered graphics is a REQUIRED reference, resolved with
      // SENT_NONE exactly like an INSERT's blockName (§Block above): AutoCAD
      // serializes a table as a block reference to that block plus the
      // AcDbTable content model, and the renderer draws the block's content.
      //
      // Mirrors the matching `extractRefs` arm (dependencyGraph.ts §Table) so
      // the forward dependency index and the audit agree about what a table
      // depends on — a block referenced ONLY by a table is in-use for PURGE
      // and a table whose graphics block is missing is reported here rather
      // than silently rendering empty.
      checkName(
        e.blockName,
        res.names.blocks,
        SENT_NONE,
        true,
        ReferenceErrorCode.UNDEFINED_BLOCK,
        base("blockName", "blockName"),
        errors,
      );
      // blockRecordHandle (343) → the BLOCK_RECORD of that SAME `*T<N>`
      // graphics block. Checked HERE, explicitly, because nothing else in the
      // audit looks at it: `checkHandleRefs` (referenceAudit.structural.ts)
      // walks block definitions, block records, layouts and the document —
      // it NEVER iterates an entity field — so the earlier note claiming "the
      // handle audit resolves it" was false, and a table whose group-2 name
      // still resolved while its 343 pointed at a purged/deleted BLOCK_RECORD
      // audited CLEAN and re-exported the stale pointer.
      //
      // OPTIONAL (`required = false`): AutoCAD writes the 2/343 pair, but a
      // bridge- or command-built table carries only the name until export
      // stamps the handle, and `checkHandle` skips an absent/empty value.
      // Resolved against the BLOCK_RECORD set, never the union (P2-B —
      // handle targets are type-specific), exactly like the Block arm's
      // `dimblk*Handle` pointers above.
      checkHandle(
        e.blockRecordHandle,
        res.handles.blockRecords,
        false,
        ReferenceErrorCode.DANGLING_HANDLE,
        base("blockRecordHandle", "blockRecordHandle"),
        errors,
      );
      // tableStyleHandle (342) points at a TABLESTYLE object in the OBJECTS
      // section. This model indexes no OBJECTS handles (the same reason
      // `checkHandleRefs` skips every `extensionDictionaryHandle`), so there
      // is no correctly-typed set to resolve it against and it stays
      // deliberately unchecked — never checked against the wrong set.
      break;
    }
    default:
      // Line / Point / Circle / Arc / Ellipse / Spline / LWPOLYLINE /
      // Revision Cloud / Wipeout / Polyline / OLE Frame — only carry the base
      // `layer` ref (already checked above); no further symbol-table
      // references.
      break;
  }
}

/**
 * FIX-2 (Codex PR-LT2 P2 — raw-entity linetype refcheck completeness gap).
 *
 * The typed entity-linetype audit (`checkEntityRefs`, field 2) only covers
 * entities the importer modelled into `space.entities` / `block.entities`.
 * UNSUPPORTED entities (ACAD_TABLE, 3DFACE, raw SPLINE, …) are preserved
 * verbatim in `document.rawEntityGroups` (ENTITIES-section siblings) and
 * `block.rawEntityGroups` (BLOCK children) and re-emitted with their original
 * DXF group-`6` linetype NAME. Without scanning those raw groups, a raw entity
 * carrying `6/GHOSTLT` would export a DANGLING linetype reference while the
 * audit reported NO `UNDEFINED_LINETYPE` — a false-negative.
 *
 * This pass mirrors the EXPORT side's `collectReferencedLinetypeNames`
 * group-6 extraction shape (dxfBridge.export.ts): scan each raw group's
 * `groups[]` for code `6` and resolve the trimmed NAME against the
 * (seed-merged) linetype table with the SAME bypass + case-insensitive
 * resolution as the typed path (`SENT_LINETYPE` skips ByLayer/ByBlock/
 * Continuous). Group 6 holds the name directly (not a handle). Optional: an
 * absent / empty 6 means "no explicit linetype" and is skipped (ByLayer is the
 * implicit default — never an error). PURE — only appends to `errors`.
 *
 * `ownerKind` identifies the raw container (`entity` for ENTITIES-section
 * siblings, `blockChild` for BLOCK children); `ownerId` uses the raw entity's
 * DXF type with its index so the issue locates the offending raw record.
 */
export function checkRawEntityLinetypeRefs(
  rawGroups: ReadonlyArray<RawEntityGroup> | undefined,
  res: Resolution,
  ownerKind: OwnerKind,
  container: string,
  errors: ReferenceIssue[],
): void {
  if (!rawGroups) return;
  rawGroups.forEach((raw, gi) => {
    for (const g of raw.groups) {
      if (g.code !== 6) continue;
      const ownerId = `${raw.entityType}[${gi}]`;
      checkName(
        g.value.trim(),
        res.names.linetypes,
        SENT_LINETYPE,
        false, // optional — an absent / empty 6 means ByLayer (no error)
        ReferenceErrorCode.UNDEFINED_LINETYPE,
        {
          ownerKind,
          ownerId,
          container,
          path: `rawEntityGroups[${gi}].groups[6]`,
          field: "linetype",
        },
        errors,
      );
    }
  });
}
